import {
  MaintenancePlanBulkImport,
  maintenancePlanBulkImportResultSchema,
} from "@boark/shared/types/maintenance-plan-bulk-import";
import { MaintenancePlanCreate } from "@boark/shared/types/maintenance-plan-create";
import { MaintenancePlanUpdate } from "@boark/shared/types/maintenance-plan-update";
import {
  MaintenancePlanWithDetails,
  maintenancePlanWithDetailsSchema,
} from "@boark/shared/types/maintenance-plan-with-details";
import * as z from "zod";

import {
  deleteRequest,
  getRequest,
  postRequest,
  putRequest,
  request,
} from "../utils/request";
import { recordIdToId } from "../utils/record-id-to-id";

const API_BASE_URL = process.env.API_BASE_URL || "/api";

function toApiId(table: string, id: string): string {
  return id.startsWith(`${table}:`) ? recordIdToId(id) : id;
}

export async function getMaintenancePlans(): Promise<
  MaintenancePlanWithDetails[]
> {
  return request(`${API_BASE_URL}/maintenance-plan`, {
    schema: z.array(maintenancePlanWithDetailsSchema),
    headers: { "Content-Type": "application/json" },
  });
}

export async function getMaintenancePlan(
  id: string,
): Promise<MaintenancePlanWithDetails> {
  return getRequest(
    `${API_BASE_URL}/maintenance-plan/${id}`,
    maintenancePlanWithDetailsSchema,
  );
}

export async function createMaintenancePlan(
  maintenancePlan: MaintenancePlanCreate,
): Promise<MaintenancePlanWithDetails> {
  return postRequest(
    `${API_BASE_URL}/maintenance-plan`,
    {
      ...maintenancePlan,
      maintenancePlanCode: toApiId(
        "maintenancePlanCode",
        maintenancePlan.maintenancePlanCode,
      ),
      propertyPart: toApiId("propertyPart", maintenancePlan.propertyPart),
    },
    maintenancePlanWithDetailsSchema,
  );
}

export async function updateMaintenancePlan(
  id: string,
  maintenancePlan: MaintenancePlanUpdate,
): Promise<MaintenancePlanWithDetails> {
  return putRequest(
    `${API_BASE_URL}/maintenance-plan/${id}`,
    {
      ...maintenancePlan,
      ...(maintenancePlan.maintenancePlanCode && {
        maintenancePlanCode: toApiId(
          "maintenancePlanCode",
          maintenancePlan.maintenancePlanCode,
        ),
      }),
      ...(maintenancePlan.propertyPart && {
        propertyPart: toApiId("propertyPart", maintenancePlan.propertyPart),
      }),
    },
    maintenancePlanWithDetailsSchema,
  );
}

export async function deleteMaintenancePlan(id: string): Promise<void> {
  return deleteRequest(`${API_BASE_URL}/maintenance-plan/${id}`);
}

export async function bulkImportMaintenancePlans(
  bulkImport: MaintenancePlanBulkImport,
) {
  return postRequest(
    `${API_BASE_URL}/maintenance-plan/bulk`,
    bulkImport,
    maintenancePlanBulkImportResultSchema,
  );
}
